import React from 'react';
import { 
  FaChartPie, 
  FaCalendarAlt, 
  FaUsers, 
  FaEnvelope, 
  FaUserClock, 
  FaChevronLeft, 
  FaChevronRight,
  FaShieldAlt,
  FaCogs,
  FaHeartbeat
} from 'react-icons/fa';

const DashboardSidebar = ({ activeTab, setActiveTab, role, collapsed, setCollapsed, submissionsCount = 0, requestsCount = 0 }) => {
  const [currentUser, setCurrentUser] = React.useState(null);

  React.useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser));
      } catch (err) {}
    }
  }, []);

  const tabs = [
    { 
      id: 'stats', 
      label: 'Analytics Dashboard', 
      icon: <FaChartPie className="text-lg" />, 
      showFor: ['admin', 'viewer', 'receptionist', 'therapist'] 
    },
    { 
      id: 'appointments', 
      label: 'Scheduler', 
      icon: <FaCalendarAlt className="text-lg" />, 
      showFor: ['admin', 'viewer', 'receptionist', 'therapist'] 
    },
    { 
      id: 'contacts', 
      label: 'Patient Inbox', 
      icon: <FaEnvelope className="text-lg" />, 
      showFor: ['admin', 'viewer', 'receptionist'] 
    },
    { 
      id: 'customer-portal', 
      label: 'Patient 360', 
      icon: <FaUsers className="text-lg" />, 
      showFor: ['admin', 'therapist'] 
    },
    { 
      id: 'team', 
      label: 'Staff Directory', 
      icon: <FaShieldAlt className="text-lg" />, 
      showFor: ['admin'] 
    },
    { 
      id: 'requests', 
      label: 'Intake Queue', 
      icon: <FaUserClock className="text-lg" />, 
      showFor: ['admin'] 
    },
  ];

  const visibleTabs = tabs.filter(tab => tab.showFor.includes(role));

  return (
    <aside 
      className={`fixed top-0 left-0 h-screen z-30 flex flex-col bg-white/80  border-r border-slate-200/60  backdrop-blur-xl transition-all duration-300 custom-grid-bg ${
        collapsed ? 'w-20' : 'w-64'
      }`}
    >
      <style>{`
        @keyframes heart-pulse {
          0% { stroke-dashoffset: 60; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes glow-sweep {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }
        .animate-heartbeat {
          stroke-dasharray: 60;
          stroke-dashoffset: 120;
          animation: heart-pulse 2.5s linear infinite;
        }
        .animate-sweep-slow {
          animation: glow-sweep 4s ease-in-out infinite;
        }
        .custom-grid-bg {
          background-image: 
            linear-gradient(rgba(147, 51, 234, 0.015) 1px, transparent 1px), 
            linear-gradient(90deg, rgba(147, 51, 234, 0.015) 1px, transparent 1px);
          background-size: 16px 16px;
        }
      `}</style>

      {/* Floating Toggle Button on Border */}
      <button 
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-6 bg-white  border border-slate-200  text-slate-400 hover:text-purple-600 :text-purple-400 hover:bg-purple-50 :bg-purple-950/30 h-6 w-6 rounded-full shadow-md flex items-center justify-center transition-all z-50 hover:scale-110 hover:border-purple-300"
      >
        {collapsed ? <FaChevronRight size={8} /> : <FaChevronLeft size={8} />}
      </button>

      {/* Brand Header */}
      <div className="flex items-center p-4 border-b border-slate-300/80  h-20 relative overflow-hidden bg-gradient-to-br from-purple-50/40 via-white/50 to-transparent   ">
        {/* Decorative corner glow */}
        <div className="absolute top-0 left-0 w-16 h-16 bg-purple-500/10 rounded-full blur-xl pointer-events-none"></div>
        <div className="absolute right-0 bottom-0 w-12 h-12 bg-indigo-500/5 rounded-full blur-lg pointer-events-none"></div>

        {!collapsed && (
          <div className="flex items-center gap-3">
            {/* Simple Logo Icon */}
            <FaHeartbeat className="text-3xl text-purple-600  drop-shadow-md animate-pulse" />
            
            <div className="flex flex-col">
              <span className="font-extrabold text-sm text-slate-800  tracking-tight leading-none bg-gradient-to-r from-slate-900 to-slate-700   bg-clip-text text-transparent">
                HealthGURU
              </span>
              <span className="text-[9px] font-extrabold text-emerald-600  bg-emerald-50  border border-emerald-100  px-2 py-0.5 rounded-full mt-1.5 uppercase flex items-center gap-1 w-max shadow-md shadow-emerald-100/50">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                Secure Link
              </span>
            </div>
          </div>
        )}

        {collapsed && (
          <div className="mx-auto flex items-center justify-center h-10 w-10">
            <FaHeartbeat className="text-2xl text-purple-600  animate-pulse" />
          </div>
        )}
      </div>

      {/* User Profile Card Section (Top of Sidebar) */}
      {!collapsed && (
        <div className="mx-3 mt-4 mb-2 p-3.5 rounded-xl bg-gradient-to-br from-slate-50/80 to-white/40   border border-slate-200/50  shadow-md relative overflow-hidden group">
          {/* Glow dot in background */}
          <div className="absolute -right-2 -bottom-2 w-12 h-12 bg-purple-500/10 rounded-full blur-md group-hover:bg-purple-500/20 transition-all duration-300 pointer-events-none"></div>
          
          <div className="flex items-center gap-3">
            {/* Avatar with Status Ring */}
            <div className="relative">
              <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-600 p-0.5 shadow-md shadow-purple-500/20">
                {currentUser?.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt="avatar" className="w-full h-full rounded-full object-cover border border-white/20" />
                ) : (
                  <div className="w-full h-full rounded-full bg-purple-100  flex items-center justify-center text-purple-700  font-extrabold text-xs">
                    {currentUser?.email ? currentUser.email.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
              </div>
              {/* Pulsing Status Dot */}
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white  ring-1 ring-emerald-400 animate-pulse"></span>
            </div>
            
            {/* User Details */}
            <div className="flex flex-col text-left overflow-hidden">
              <span className="font-bold text-slate-800  text-[11px] truncate w-32" title={currentUser?.email}>
                {currentUser?.email ? currentUser.email.split('@')[0] : 'User'}
              </span>
              <span className="text-[9px] text-slate-400  truncate max-w-[120px]">
                {currentUser?.email || 'user@healthguru.com'}
              </span>
              
              {/* Dynamic Badge */}
              <div className="mt-1 flex items-center">
                {role === 'admin' && (
                  <span className="text-[8px] font-black tracking-widest text-purple-700  bg-purple-100  px-1.5 py-0.5 rounded border border-purple-200  uppercase">
                    🛡️ Chief Admin
                  </span>
                )}
                {role === 'therapist' && (
                  <span className="text-[8px] font-black tracking-widest text-teal-700  bg-teal-100  px-1.5 py-0.5 rounded border border-teal-200  uppercase">
                    ⚕️ Therapist
                  </span>
                )}
                {role === 'receptionist' && (
                  <span className="text-[8px] font-black tracking-widest text-rose-700  bg-rose-100  px-1.5 py-0.5 rounded border border-rose-200  uppercase">
                    📋 Coordinator
                  </span>
                )}
                {role === 'viewer' && (
                  <span className="text-[8px] font-black tracking-widest text-slate-600  bg-slate-100  px-1.5 py-0.5 rounded border border-slate-200  uppercase">
                    👁️ Auditor
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {collapsed && (
        <div className="my-4 flex justify-center">
          <div className="relative group cursor-pointer">
            <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-600 p-0.5 shadow-md shadow-purple-500/20">
              {currentUser?.avatarUrl ? (
                <img src={currentUser.avatarUrl} alt="avatar" className="w-full h-full rounded-full object-cover border border-white/20" />
              ) : (
                <div className="w-full h-full rounded-full bg-purple-100  flex items-center justify-center text-purple-700  font-extrabold text-xs">
                  {currentUser?.email ? currentUser.email.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
            </div>
            <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white  ring-1 ring-emerald-400 animate-pulse"></span>
            
            {/* Tooltip on Hover */}
            <div className="absolute left-14 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-[10px] font-bold py-1 px-2 rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
              {currentUser?.email || 'User'} ({role})
            </div>
          </div>
        </div>
      )}


      {/* Nav Links */}
      <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto">
        <div className="text-slate-400  text-[9px] font-black uppercase tracking-widest px-3.5 mb-3 flex items-center gap-1.5">
          <span className="h-1 w-1 bg-purple-500 rounded-full"></span>
          {!collapsed ? 'Clinical Operations' : 'Ops'}
        </div>
        {visibleTabs.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl font-bold text-xs tracking-wide transition-all duration-300 relative group overflow-hidden ${
                isActive 
                  ? 'bg-gradient-to-r from-purple-500/10 to-indigo-500/5   text-purple-700  border-l-4 border-purple-600  shadow-md shadow-purple-500/5'
                  : 'text-slate-600  hover:text-purple-700 :text-purple-300 hover:bg-purple-50/40 :bg-purple-950/20'
              }`}
            >
              {/* Highlight bar effect */}
              {isActive && (
                <span className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-purple-600  mr-2 shadow shadow-purple-400/50"></span>
              )}
              
              {/* Scale icon on active or hover */}
              <div className={`transition-transform duration-300 group-hover:scale-110 relative ${isActive ? 'text-purple-600 ' : 'text-slate-400  group-hover:text-purple-500'}`}>
                {tab.icon}
                
                {/* Collapsed dot notifications */}
                {collapsed && tab.id === 'contacts' && submissionsCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-purple-600 border border-white  shadow shadow-purple-500/50"></span>
                )}
                {collapsed && tab.id === 'requests' && requestsCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-rose-500 border border-white  shadow shadow-rose-500/50"></span>
                )}
              </div>
              
              {!collapsed && (
                <span className="truncate transition-colors duration-300">{tab.label}</span>
              )}

              {/* Expanded Counter Badges */}
              {!collapsed && tab.id === 'contacts' && submissionsCount > 0 && (
                <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-black bg-purple-600 text-white shadow-md shadow-purple-500/30 animate-pulse">
                  {submissionsCount}
                </span>
              )}
              {!collapsed && tab.id === 'requests' && requestsCount > 0 && (
                <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-black bg-rose-500 text-white shadow-md shadow-rose-500/30 animate-pulse">
                  {requestsCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom Graphic Widget Card */}
      {!collapsed && (
        <div className="mx-3 mb-4 p-4 rounded-xl bg-gradient-to-br from-purple-900 to-indigo-950 text-white relative overflow-hidden shadow-md border border-purple-800/30">
          {/* Abstract background graphics */}
          <div className="absolute right-0 bottom-0 w-24 h-24 bg-purple-500/10 rounded-full blur-xl pointer-events-none"></div>
          <div className="absolute left-0 top-0 w-16 h-16 bg-emerald-500/10 rounded-full blur-lg pointer-events-none"></div>
          
          <div className="relative z-10 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-purple-300 bg-purple-800/40 px-2 py-0.5 rounded-full border border-purple-700/50">
                Security Shield
              </span>
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            </div>
            
            <div>
              <h4 className="text-xs font-black tracking-tight">HIPAA Compliant</h4>
              <p className="text-[10px] text-purple-200/70 mt-0.5 font-medium leading-relaxed">
                Database transaction shielding & auto-masking is active.
              </p>
            </div>

            {/* Renders a nice health bar */}
            <div className="mt-1">
              <div className="flex justify-between text-[9px] font-bold text-purple-300 mb-0.5">
                <span>Shield Integrity</span>
                <span>100%</span>
              </div>
              <div className="w-full bg-purple-950/60 h-1 rounded-full overflow-hidden border border-purple-800/20">
                <div className="bg-gradient-to-r from-emerald-400 to-purple-400 h-full rounded-full w-full"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer Settings Toggle Indicator */}
      <div className="p-3 border-t border-slate-300  flex flex-col gap-2 bg-slate-50/30 ">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} text-slate-500 hover:text-purple-700  :text-purple-300 hover:bg-purple-50/50 :bg-purple-950/25 p-2.5 rounded-xl text-xs font-bold cursor-pointer transition-all duration-200 group`}>
          <FaCogs className="text-lg text-slate-400  group-hover:text-purple-600 :text-purple-400 group-hover:rotate-45 transition-all duration-300" />
          {!collapsed && <span className="transition-colors">Settings & Logs</span>}
        </div>
      </div>
    </aside>
  );
};

export default DashboardSidebar;
